import { createPilotControl } from "./createPilotControl";
import { createPilotFeatureLifecycle } from "./createPilotFeatureLifecycle";
import {
  createMemoryRolloutPolicyProvider,
  createRolloutPolicyReader,
} from "./createRolloutPolicyReader";
import { createMemoryPilotTelemetry } from "./pilotTelemetryAdapters";

const CODE_PATTERN = /^[a-z0-9][a-z0-9._-]{0,119}$/i;
const PRIVATE_SUBJECT = "pilot-dry-run-subject";
const PRIVATE_MARKERS = Object.freeze([
  "context",
  "email",
  "href",
  "subject",
  "url",
  "userId",
]);

const deepFreeze = (value) => {
  if (!value || typeof value !== "object" || Object.isFrozen(value)) return value;
  Object.values(value).forEach(deepFreeze);
  return Object.freeze(value);
};

const requireCode = (value, key) => {
  if (typeof value !== "string" || !CODE_PATTERN.test(value)) {
    throw new TypeError(`${key} must be a structured code`);
  }
  return value;
};

const decisionFor = ({ destinationId, pilot, readiness, rollout }) => (
  pilot.decide({
    destinationId,
    preferredVersion: "v2",
    readiness,
    rollout,
  })
);

const createPolicy = ({
  cohortPercentage,
  configurationRevision,
  destinationId,
  expiresAt,
  globalV2Enabled,
  release,
}) => ({
  schemaVersion: 1,
  release,
  configurationRevision,
  globalV2Enabled,
  cohortPercentage,
  enabledDestinations: [destinationId],
  expiresAt,
});

const readPolicy = ({ bucket, now, policy, release }) => (
  createRolloutPolicyReader({
    bucket,
    now,
    provider: createMemoryRolloutPolicyProvider({
      policy,
      subject: PRIVATE_SUBJECT,
    }),
  }).read({ release })
);

const createBusinessCommitProbe = () => {
  const commits = [];
  return Object.freeze({
    commit(version) {
      commits.push(Object.freeze({ version }));
      return Object.freeze({ status: "committed", version });
    },
    read() {
      return Object.freeze([...commits]);
    },
  });
};

const createRecoveryProbe = ({ destinationId, pilot }) => {
  let settled = false;
  let attempts = 0;
  return Object.freeze({
    async recover() {
      attempts += 1;
      if (settled) return Object.freeze({ status: "ignored", reason: "already-recovered" });
      settled = true;
      await pilot.record({
        type: "fallback",
        destinationId,
        reason: "pre-render-load",
        context: { zooId: "must-not-leak" },
        email: "must-not-leak@example.org",
        url: "/v2/private?query=must-not-leak",
        userId: "must-not-leak",
      });
      return Object.freeze({ status: "recovered", version: "v1" });
    },
    readAttempts() {
      return attempts;
    },
  });
};

const privacySafe = (events) => {
  const serialized = JSON.stringify(events);
  return PRIVATE_MARKERS.every((key) => !serialized.includes(`"${key}"`)) &&
    !serialized.includes("must-not-leak") &&
    !serialized.includes(PRIVATE_SUBJECT);
};

const check = (id, passed) => Object.freeze({
  id,
  status: passed ? "passed" : "failed",
});

/**
 * Deterministic, side-effect-free T23 contract rehearsal.
 *
 * The harness uses only in-memory policy, telemetry, mutation and recovery
 * probes. It executes the public pilot seams and returns a privacy-safe proof
 * certificate; it never receives an endpoint, credential or business payload.
 * This is deliberately not operational evidence for a real connector or a
 * production dry-run.
 */
export const createPilotDryRunHarness = ({ now = () => 1724148000000 } = {}) => {
  if (typeof now !== "function") throw new TypeError("now must be a function");

  return Object.freeze({
    async run({
      configurationRevision = "dry-run.1",
      destinationId,
      release = "dry-run",
    } = {}) {
      requireCode(destinationId, "destinationId");
      requireCode(configurationRevision, "configurationRevision");
      requireCode(release, "release");
      const occurredAt = now();
      if (!Number.isFinite(occurredAt) || occurredAt < 0) {
        throw new TypeError("now must return a positive finite timestamp or zero");
      }

      const clock = () => occurredAt;
      const expiresAt = occurredAt + 60000;
      const telemetry = createMemoryPilotTelemetry();
      const pilot = createPilotControl({ telemetry, now: clock });
      const enabledPolicy = await readPolicy({
        bucket: () => 0,
        now: clock,
        policy: createPolicy({
          cohortPercentage: 100,
          configurationRevision,
          destinationId,
          expiresAt,
          globalV2Enabled: true,
          release,
        }),
        release,
      });
      const closedCohortPolicy = await readPolicy({
        bucket: () => 0,
        now: clock,
        policy: createPolicy({
          cohortPercentage: 0,
          configurationRevision,
          destinationId,
          expiresAt,
          globalV2Enabled: true,
          release,
        }),
        release,
      });
      const killSwitchPolicy = await readPolicy({
        bucket: () => 0,
        now: clock,
        policy: createPolicy({
          cohortPercentage: 100,
          configurationRevision,
          destinationId,
          expiresAt,
          globalV2Enabled: false,
          release,
        }),
        release,
      });

      const decisions = {
        approvedOpen: decisionFor({
          destinationId,
          pilot,
          readiness: "parity-approved",
          rollout: enabledPolicy,
        }),
        candidateClosed: decisionFor({
          destinationId,
          pilot,
          readiness: "candidate",
          rollout: enabledPolicy,
        }),
        candidatePreviewOpen: decisionFor({
          destinationId,
          pilot,
          readiness: "candidate",
          rollout: { ...enabledPolicy, previewCandidates: true },
        }),
        cohortClosed: decisionFor({
          destinationId,
          pilot,
          readiness: "parity-approved",
          rollout: closedCohortPolicy,
        }),
        killSwitchClosed: decisionFor({
          destinationId,
          pilot,
          readiness: "candidate",
          rollout: { ...killSwitchPolicy, previewCandidates: true },
        }),
      };

      const recovery = createRecoveryProbe({ destinationId, pilot });
      const firstRecovery = await recovery.recover();
      const duplicateRecovery = await recovery.recover();

      const business = createBusinessCommitProbe();
      business.commit(decisions.approvedOpen.version);

      const featureLifecycle = createPilotFeatureLifecycle({
        now: clock,
        record: pilot.record,
      });
      featureLifecycle.begin({
        destinationId,
        navigationRevision: 1,
        readiness: "candidate",
        startedAt: occurredAt,
        version: decisions.candidatePreviewOpen.version,
      });
      const firstReady = featureLifecycle.reportReady({ navigationRevision: 1 });
      const duplicateReady = featureLifecycle.reportReady({ navigationRevision: 1 });

      const events = telemetry.read();
      const commits = business.read();
      const checks = [
        check(
          "candidate-production-closed",
          decisions.candidateClosed.version === "v1" &&
            decisions.candidateClosed.reason === "v2-not-approved"
        ),
        check(
          "candidate-preview-open",
          decisions.candidatePreviewOpen.version === "v2" &&
            decisions.candidatePreviewOpen.reason === "development-preview"
        ),
        check(
          "parity-approved-policy-open",
          decisions.approvedOpen.version === "v2"
        ),
        check(
          "cohort-policy-closed",
          decisions.cohortClosed.version === "v1" &&
            decisions.cohortClosed.reason === "cohort-disabled"
        ),
        check(
          "kill-switch-overrides-preview",
          decisions.killSwitchClosed.version === "v1" &&
            decisions.killSwitchClosed.reason === "global-kill-switch"
        ),
        check(
          "pre-render-recovery-once",
          firstRecovery.status === "recovered" &&
            duplicateRecovery.reason === "already-recovered" &&
            recovery.readAttempts() === 2 &&
            events.filter((event) => event.type === "fallback").length === 1
        ),
        check(
          "telemetry-privacy-allowlist",
          privacySafe(events)
        ),
        check(
          "single-in-memory-commit-probe",
          commits.length === 1 && commits[0].version === "v2"
        ),
        check(
          "feature-stable-exactly-once",
          firstReady.status === "reported" &&
            duplicateReady.reason === "already-settled" &&
            events.filter((event) => (
              event.type === "render" && event.phase === "feature-stable"
            )).length === 1
        ),
      ];

      return deepFreeze({
        status: checks.every((result) => result.status === "passed")
          ? "passed"
          : "failed",
        destinationId,
        release,
        configurationRevision,
        checks,
        evidence: {
          decisions,
          recovery: {
            attempts: recovery.readAttempts(),
            first: firstRecovery,
            duplicate: duplicateRecovery,
          },
          businessCommits: commits,
          telemetry: events,
        },
      });
    },
  });
};
